import { useRef, useState, useCallback, useEffect } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp, mediaQueryDown } from 'components/utils/breakpoint'
import IconButton from 'LuxKit/Button/IconButton'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import LineEllipsisHIcon from 'LuxKit/Icons/line/LineEllipsisHIcon'
import LinePenIcon from 'LuxKit/Icons/line/LinePenIcon'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import LineArrowUpIcon from 'LuxKit/Icons/line/LineArrowUpIcon'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import useToggle from 'hooks/useToggle'
import moment from 'moment'
import BorderlessTextAreaOnly from 'components/Common/Form/Input/BorderlessTextAreaOnly'
import { COMMENT_MAX_LENGTH } from 'constants/comments'
import CharacterLimitWarning from './CharacterLimitWarning'

const CommentCard = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

const ContentContainer = styled.div`
  flex: 1;
  min-width: 0;
`

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  min-height: ${rem(16)};
`

const HeaderText = styled(Caption).attrs({ variant: 'small' as const })`
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  line-height: 1;
  display: flex;
  align-items: center;
`

const MenuButtonContainer = styled.div`
  flex-shrink: 0;
  margin-inline-start: ${rem(8)};
  align-self: center;
`

const MenuButton = styled(IconButton)`
  opacity: 0;
  transition: opacity 0.2s;
  --button-border-color: ${props => props.theme.palette.neutral.surface.lightForeground};
  --button-hover-border-color: ${props => props.theme.palette.neutral.surface.lightForeground};
  --button-focus-border-color: ${props => props.theme.palette.neutral.surface.lightForeground};
  --button-active-border-color: ${props => props.theme.palette.neutral.surface.lightForeground};
  
  &[aria-expanded="true"] {
    opacity: 1;
  }
  
  ${CommentCard}:hover & {
    ${mediaQueryUp.tablet} {
      opacity: 1;
    }
  }
  ${mediaQueryDown.mobile} {
    opacity: 1;
  }
`

const CommentText = styled(BodyText).attrs({ variant: 'medium' as const })`
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  padding: 0;
`

const EditInputContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
`

interface Props {
  comment: App.MultiCartComment
  currentUserId?: string
  isEditing?: boolean
  onEdit: (comment: App.MultiCartComment) => void
  onDelete: (comment: App.MultiCartComment) => void
  onUpdateComment: (commentId: string, text: string) => void
  onCancelEdit: (commentId: string) => void
  onEmptyEdit: (comment: App.MultiCartComment) => void
}

export default function CommentItem({
  comment,
  currentUserId,
  isEditing = false,
  onEdit,
  onDelete,
  onUpdateComment,
  onCancelEdit,
  onEmptyEdit,
}: Props) {
  const intl = useIntl()
  const { value: isMenuOpen, toggle: toggleMenu, off: closeMenu } = useToggle(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const [editText, setEditText] = useState(comment.text)
  const editInputRef = useRef<HTMLTextAreaElement>(null)

  const canEdit = comment.createdBy.idMember === currentUserId

  useEffect(() => {
    if (isEditing) {
      setEditText(comment.text)
      editInputRef.current?.focus()
    }
  }, [isEditing, comment.text])

  const handleEdit = useCallback(() => {
    onEdit(comment)
    closeMenu()
  }, [comment, onEdit, closeMenu])

  const handleDelete = useCallback(() => {
    onDelete(comment)
    closeMenu()
  }, [comment, onDelete, closeMenu])

  const handleEditChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditText(e.target.value)
  }, [])

  const handleEditSubmit = useCallback(() => {
    const trimmedText = editText.trim()
    if (trimmedText.length === 0) {
      onEmptyEdit(comment)
    } else {
      onUpdateComment(comment.id, trimmedText)
    }
  }, [editText, comment, onUpdateComment, onEmptyEdit])

  const handleEditCancel = useCallback(() => {
    setEditText(comment.text)
    onCancelEdit(comment.id)
  }, [comment.text, comment.id, onCancelEdit])

  const handleEditKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleEditSubmit()
    } else if (e.key === 'Escape') {
      handleEditCancel()
    }
  }, [handleEditSubmit, handleEditCancel])

  // Show "You" for current user, otherwise show first name
  const displayName = canEdit
    ? intl.formatMessage({
        id: 'MR5vedDzDFTEoy6pMgWx',
        defaultMessage: 'You',
        description: 'Display name for the current user in comments in the shopping cart',
      })
    : comment.createdBy.givenName ||
      comment.createdBy.email ||
      intl.formatMessage({
        id: 'SmWDzUD2CFhNY1LAdcVb',
        defaultMessage: 'Unknown User',
        description: 'Fallback display name when user name is not available',
      })

  const momentCreatedAt = moment(comment.createdAt)

  const editCharacterCount = editText.length
  const isEditOverLimit = editCharacterCount >= COMMENT_MAX_LENGTH

  const timeAgo = momentCreatedAt.fromNow()
  const commentHeader = comment.editedAt
    ? intl.formatMessage(
        {
          id: 'FHOimcn_i8C0wdUthvW1',
          defaultMessage: '{name} • {timeAgo} • Edited',
          description: 'Comment metadata showing author, relative time, and edited indicator',
        },
        { name: displayName, timeAgo },
      )
    : intl.formatMessage(
        {
          id: 'mJ9P1Eqlb7ZsMfZkEg-h',
          defaultMessage: '{name} • {timeAgo}',
          description: 'Comment metadata showing author and relative time in the shopping cart',
        },
        { name: displayName, timeAgo },
      )

  if (isEditing) {
    return (
      <CommentCard>
        <ContentContainer>
          <HeaderContainer>
            <HeaderText variant="small">
              {commentHeader}
            </HeaderText>
          </HeaderContainer>
          <Group direction="horizontal" gap={8} verticalAlign="center">
            <EditInputContainer>
              <BorderlessTextAreaOnly
                ref={editInputRef}
                value={editText}
                onChange={handleEditChange}
                onKeyDown={handleEditKeyDown}
                placeholder={intl.formatMessage({
                  id: '26G7-UmKUhRmIrnR9X78',
                  defaultMessage: 'Edit comment...',
                  description: 'Input placeholder text for editing a comment in the shopping cart',
                })}
                maxLength={COMMENT_MAX_LENGTH}
              />
              {isEditOverLimit && (
                <CharacterLimitWarning characterCount={editCharacterCount} maxLength={COMMENT_MAX_LENGTH} />
              )}
            </EditInputContainer>
            <Group direction="horizontal" gap={8} verticalAlign="center" shrink={false}>
              <IconButton
                kind="tertiary"
                shape="circle"
                size="small"
                onClick={handleEditCancel}
                aria-label={intl.formatMessage({
                  id: 'aKyRZCEDPrD-SoXN83z1',
                  defaultMessage: 'Cancel',
                  description: 'Aria label for cancel edit comment button in the cancellation flow',
                })}
              >
                <LineTimesIcon size="S" />
              </IconButton>
              <IconButton
                kind="primary"
                shape="circle"
                size="small"
                onClick={handleEditSubmit}
                aria-label={intl.formatMessage({
                  id: 'Z9cDyoBmDyv8Vf_FHEEk',
                  defaultMessage: 'Save comment',
                  description: 'Accessibility aria-label for save edited comment button',
                })}
              >
                <LineArrowUpIcon size="S" />
              </IconButton>
            </Group>
          </Group>
        </ContentContainer>
      </CommentCard>
    )
  }

  return (
    <CommentCard>
      <ContentContainer>
        <HeaderContainer>
          <HeaderText variant="small">
            {commentHeader}
          </HeaderText>
        </HeaderContainer>
        <CommentText variant="medium">{comment.text}</CommentText>
      </ContentContainer>
      {canEdit && (
        <MenuButtonContainer>
          <MenuButton
            kind="tertiary"
            shape="circle"
            size="small"
            onClick={toggleMenu}
            ref={menuButtonRef}
            aria-label={intl.formatMessage({
              id: 'S8t0r1j-DjgnbPgSJUiV',
              defaultMessage: 'Comment options',
              description: 'Accessibility aria-label for comment options menu button',
            })}
            aria-expanded={isMenuOpen}
          >
            <LineEllipsisHIcon size="S" />
          </MenuButton>
          <DropdownList
            size="S"
            triggerRef={menuButtonRef}
            anchorRef={menuButtonRef}
            open={isMenuOpen}
            onClose={closeMenu}
            placement="bottom-end"
            portaled
          >
            {canEdit && (
              <ClickableListItem
                size="medium"
                title={intl.formatMessage({
                  id: 'kjSlPW1v9i-4uerQfMfC',
                  defaultMessage: 'Edit',
                  description: 'Context menu item to edit a user comment on a trip planner shared item card',
                })}
                startIcon={<LinePenIcon />}
                onClick={handleEdit}
              />
            )}
            <ClickableListItem
              size="medium"
              title={intl.formatMessage({
                id: 'D6l9ALdU2TQc1lPgQ787',
                defaultMessage: 'Delete',
                description: 'Context menu item to delete a user comment on a trip planner shared item',
              })}
              titleColour="critical"
              startIcon={<LineTrashAltIcon colour="critical" />}
              onClick={handleDelete}
            />
          </DropdownList>
        </MenuButtonContainer>
      )}
    </CommentCard>
  )
}
